/**
 * EntityIndex — store-backed spatial index over the CAD entity ledger.
 *
 * Wraps the OCS-style QuadTree with the two-lane synchronization the
 * reference implementation uses:
 *  1. Epoch path — when the changed-entity count since the last sync grows
 *     past the patch budget, the whole tree is rebuilt (root = padded union
 *     of all bounds).
 *  2. Incremental patch path — small edit sets diff against the indexed
 *     entity snapshot and issue Remove/Insert operations only, keeping the
 *     tree warm during interactive drafting.
 *
 * Entities without boundable geometry (inserts, viewports, degenerate
 * boxes) live in the `unbounded` bag and are returned from every rect
 * query — a query result is always a superset of the true hits, so
 * consumers refine with their own exact tests.
 */

import { useAppStore, type Entity } from '../../state/store';
import { QuadTree, padRootBounds, type AABB } from './quadtree';
import { entityAABB } from './entityBounds';

/**
 * Diffed changes above this count switch the sync to a full rebuild
 * (OCS replay_since fallback: an affordable patch keeps the tree warm,
 * a big divergence is cheaper to rebuild than to replay).
 */
const PATCH_BUDGET = 1024;

export interface EntityIndexStats {
    indexed: number;
    unbounded: number;
    treeNodes: number;
    maxDepth: number;
    /** Monotonic counter — bumped on every store-sync (patch or rebuild). */
    epoch: number;
    rebuilds: number;
    patches: number;
}

export class EntityIndex {
    private tree = new QuadTree();
    /** Entity reference per indexed id — the diff baseline. */
    private snapshot = new Map<string, Entity>();
    /** Unboundable entities (insert, viewport, degenerate geometry). */
    private unbounded = new Set<string>();
    private epochCounter = 0;
    private rebuildCounter = 0;
    private patchCounter = 0;
    private unsubscribe: (() => void) | null = null;

    start(): void {
        if (this.unsubscribe) return;
        this.sync(useAppStore.getState().entities);
        this.unsubscribe = useAppStore.subscribe((state, prev) => {
            if (state.entities === prev.entities) return;
            this.sync(state.entities);
        });
    }

    stop(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }

    /** All entity ids whose bounds intersect the world-XY rect (superset). */
    queryRect(rect: AABB): string[] {
        const out = this.tree.queryRect(rect);
        for (const id of this.unbounded) out.push(id);
        return out;
    }

    /** Number of entities with a boundable, finite AABB in the tree. */
    get indexedCount(): number {
        return this.snapshot.size - this.unbounded.size;
    }

    stats(): EntityIndexStats {
        const s = this.tree.stats();
        return {
            indexed: this.indexedCount,
            unbounded: this.unbounded.size,
            treeNodes: s.nodes,
            maxDepth: s.maxDepth,
            epoch: this.epochCounter,
            rebuilds: this.rebuildCounter,
            patches: this.patchCounter,
        };
    }

    // -- synchronization ----------------------------------------------------

    private sync(entities: Record<string, Entity>): void {
        this.epochCounter++;
        // Single pass diff against the snapshot: count the divergence first,
        // then either patch or rebuild (patching half a divergent set before
        // discovering a rebuild was needed would leave the tree transiently
        // wrong — compute, then commit).
        let diverged = 0;
        let removals = 0;
        for (const id of this.snapshot.keys()) {
            if (entities[id] === undefined) removals++;
        }
        for (const id in entities) {
            const prev = this.snapshot.get(id);
            if (prev === undefined || prev !== entities[id]) diverged++;
        }
        const total = diverged + removals;
        if (total === 0) return;
        if (total > PATCH_BUDGET) {
            this.rebuild(entities);
        } else {
            this.patch(entities, removals);
        }
    }

    private rebuild(entities: Record<string, Entity>): void {
        this.rebuildCounter++;
        this.snapshot.clear();
        this.unbounded.clear();
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const bounded: { handle: string; bounds: AABB }[] = [];
        for (const id in entities) {
            const e = entities[id]!;
            this.snapshot.set(id, e);
            const bounds = entityAABB(e);
            if (!bounds) {
                this.unbounded.add(id);
                continue;
            }
            bounded.push({ handle: id, bounds });
            if (bounds[0] < minX) minX = bounds[0];
            if (bounds[1] < minY) minY = bounds[1];
            if (bounds[2] > maxX) maxX = bounds[2];
            if (bounds[3] > maxY) maxY = bounds[3];
        }
        if (bounded.length === 0) {
            this.tree = new QuadTree();
            return;
        }
        this.tree = new QuadTree(padRootBounds(minX, minY, maxX, maxY));
        for (const item of bounded) this.tree.insert(item);
    }

    private patch(entities: Record<string, Entity>, knownRemovals: number): void {
        this.patchCounter++;
        // Removals first (cheap O(1) tree ops), then re-anchors.
        if (knownRemovals > 0) {
            for (const id of Array.from(this.snapshot.keys())) {
                if (entities[id] !== undefined) continue;
                this.snapshot.delete(id);
                this.unbounded.delete(id);
                this.tree.remove(id);
            }
        }
        for (const id in entities) {
            const e = entities[id]!;
            const prev = this.snapshot.get(id);
            if (prev === e) continue;
            this.snapshot.set(id, e);
            const bounds = entityAABB(e);
            if (!bounds) {
                if (!this.unbounded.has(id)) {
                    this.unbounded.add(id);
                    this.tree.remove(id);
                }
                continue;
            }
            if (this.unbounded.delete(id)) {
                this.tree.insert({ handle: id, bounds });
            } else {
                this.tree.update(id, bounds);
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Singleton wiring (mirrors tools/snapping/snapSystemSync.ts boot pattern)
// ---------------------------------------------------------------------------

let singleton: EntityIndex | null = null;

export function getEntityIndex(): EntityIndex {
    if (!singleton) singleton = new EntityIndex();
    return singleton;
}

/** Idempotent boot: subscribe the index to the app store. */
export function startEntityIndex(): void {
    getEntityIndex().start();
}
